import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import { toolService } from '../../services/toolService';
import type { ToolExecutionLogDto } from '../../types/tool';

export interface ToolExecutionHistoryProps {
  toolId: string;
}

/**
 * Displays a tool's execution history.
 */
export const ToolExecutionHistory = ({ toolId }: ToolExecutionHistoryProps) => {
  const [items, setItems] = useState<ToolExecutionLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await toolService.getToolExecutions(toolId, pageNumber, pageSize);
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load execution history');
    } finally {
      setLoading(false);
    }
  };

  const download = (filename: string, content: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJson = () => {
    download(`tool-${toolId}-executions.json`, JSON.stringify(items, null, 2), 'application/json');
  };

  const exportCsv = () => {
    const header = [
      'id',
      'executedAt',
      'success',
      'isDryRun',
      'executionTimeMs',
      'toolName',
      'toolKey',
      'errorMessage',
    ];

    const escape = (value: unknown) => {
      const s = value === null || value === undefined ? '' : String(value);
      return `"${s.replaceAll('"', '""')}"`;
    };

    const rows = items.map((e) =>
      [
        e.id,
        e.executedAt,
        e.success,
        e.isDryRun,
        e.executionTimeMs,
        e.toolName,
        e.toolKey,
        e.errorMessage || '',
      ].map(escape).join(',')
    );

    download(`tool-${toolId}-executions.csv`, [header.join(','), ...rows].join('\n'), 'text/csv');
  };

  useEffect(() => {
    if (toolId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, pageNumber]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No executions recorded yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Executions</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={exportJson} disabled={items.length === 0}>
            Export JSON
          </Button>
          <Button size="small" variant="outlined" onClick={exportCsv} disabled={items.length === 0}>
            Export CSV
          </Button>
        </Box>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Executed At</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Dry-run</TableCell>
            <TableCell>Time (ms)</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((e) => (
            <TableRow key={e.id} hover>
              <TableCell>{new Date(e.executedAt).toLocaleString()}</TableCell>
              <TableCell>
                <Chip
                  label={e.success ? 'Success' : 'Failed'}
                  size="small"
                  color={e.success ? 'success' : 'error'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={e.isDryRun ? 'Yes' : 'No'}
                  size="small"
                  color={e.isDryRun ? 'warning' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{e.executionTimeMs}</TableCell>
              <TableCell>
                <Typography variant="caption" color={e.success ? 'text.secondary' : 'error'}>
                  {e.errorMessage || '-'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalCount > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={pageNumber}
            onChange={(_, p) => setPageNumber(p)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

